import type {
  LifeExpectancyData,
  RegionRecord,
  Sex,
  UsRace,
} from "../adapters/life-expectancy-port";

const SEXES: readonly Sex[] = ["female", "male"];
const RACES: readonly UsRace[] = ["white", "black", "hispanic", "asian", "native"];
const REGION_KINDS: readonly RegionRecord["kind"][] = ["country", "us-state"];

export function parseLifeExpectancyData(raw: unknown): LifeExpectancyData {
  const obj = expectObject(raw, "root");
  const version = expectString(obj.version, "version");
  const regionsRaw = obj.regions;
  if (!Array.isArray(regionsRaw)) {
    throw new Error("life-expectancy: regions must be an array");
  }
  const regions = regionsRaw.map((r, i) => parseRegion(r, `regions[${i}]`));
  return { version, regions };
}

function parseRegion(raw: unknown, path: string): RegionRecord {
  const obj = expectObject(raw, path);
  const kind = expectString(obj.kind, `${path}.kind`);
  if (!(REGION_KINDS as readonly string[]).includes(kind)) {
    throw new Error(`${path}.kind must be one of ${REGION_KINDS.join("|")}, got ${kind}`);
  }
  const code = expectString(obj.code, `${path}.code`);
  const label = expectString(obj.label, `${path}.label`);
  const expectancy = parseSexMap(obj.expectancy, `${path}.expectancy`);

  const base: RegionRecord = {
    kind: kind as RegionRecord["kind"],
    code,
    label,
    expectancy,
  };
  if (obj.byRace !== undefined) {
    base.byRace = parseRaceMap(obj.byRace, `${path}.byRace`);
  }
  return base;
}

function parseSexMap(raw: unknown, path: string): Record<Sex, number> {
  const obj = expectObject(raw, path);
  const out = {} as Record<Sex, number>;
  for (const sex of SEXES) {
    out[sex] = expectNumber(obj[sex], `${path}.${sex}`);
  }
  return out;
}

function parseRaceMap(raw: unknown, path: string): Record<UsRace, Record<Sex, number>> {
  const obj = expectObject(raw, path);
  const out = {} as Record<UsRace, Record<Sex, number>>;
  for (const race of RACES) {
    out[race] = parseSexMap(obj[race], `${path}.${race}`);
  }
  return out;
}

function expectObject(raw: unknown, path: string): Record<string, unknown> {
  if (raw === null || typeof raw !== "object" || Array.isArray(raw)) {
    throw new Error(`${path} must be an object`);
  }
  return raw as Record<string, unknown>;
}

function expectString(raw: unknown, path: string): string {
  if (typeof raw !== "string") throw new Error(`${path} must be a string`);
  return raw;
}

function expectNumber(raw: unknown, path: string): number {
  if (typeof raw !== "number" || !Number.isFinite(raw)) {
    throw new Error(`${path} must be a finite number`);
  }
  return raw;
}
